import { ResponsiveHeroMedia, StandHomeTemplate } from "./StandHomeTemplate";
import type { TemplateStand } from "./StandHomeTemplate";
import type { CSSProperties } from "react";
import { useState } from "react";
import { PublicVehicleCatalogControls, PublicVehicleCategoryRail, emptyPublicVehicleFilters, filterPublicVehicles } from "./PublicVehicleCatalog";
import type { PublicCatalogVehicle, PublicVehicleCatalogData } from "./PublicVehicleCatalog";
import { defaultPublicVehicleSettings, publicVehicleBadgeLabels } from "./StandPublicVehicleSettings";
import type { PublicVehicleSettings } from "./StandPublicVehicleSettings";
import type { UnifiedPublicSiteSnapshot } from "./public-site-contract";
import { VehicleCard } from "./PublicStandSite";
import { PublicCustomerToolbar } from "./PublicCustomerTools";
import "./stand-home-templates.css";
import "./template-reference-fidelity.css";
import "./stand-branches.css";
import "./stand-location-panel.css";
import "./stand-site-preview-modal.css";
import "./stand-site-preview-layer-fix.css";
import "./demo-vehicle-images.css";

type Content = {
  identity: { name: string; tagline: string };
  hero: { headline: string; intro: string; button: string };
  colors: { accent: string };
  pages: { slug: string; title: string; status?: string }[];
};

const samples: PublicCatalogVehicle[] = [
  {
    make: "Volvo",
    model: "XC40",
    id:"preview-volvo",version:null,registration_year:2023,fuel_type:"Híbrido",fuel_key:"hybrid",transmission:"Automática",transmission_key:"automatic",mileage_km:32400,sale_price_cents:3890000,currency_code:"EUR",featured:true,body_type:"SUV",body_key:"suv",category_keys:["suv","hybrid"],first_registration_country_code:"PT",warranty_months:24,vat_deductible:true,published_at:new Date().toISOString(),badges:[],primary_photo_path:null,chat_code:null,availability:"available",
  },
  {
    make: "Mercedes-Benz",
    model: "A 180",
    id:"preview-mercedes",version:null,registration_year:2022,fuel_type:"Diesel",fuel_key:"diesel",transmission:"Automática",transmission_key:"automatic",mileage_km:41850,sale_price_cents:3175000,currency_code:"EUR",featured:false,body_type:"Citadino",body_key:"city",category_keys:["city"],first_registration_country_code:"DE",warranty_months:18,vat_deductible:false,published_at:"2024-01-01T00:00:00Z",badges:[],primary_photo_path:null,chat_code:null,availability:"reserved",
  },
  {
    make: "BMW",
    model: "320e",
    id:"preview-bmw",version:null,registration_year:2023,fuel_type:"Elétrico",fuel_key:"electric",transmission:"Automática",transmission_key:"automatic",mileage_km:28100,sale_price_cents:4250000,currency_code:"EUR",featured:false,body_type:"Berlina",body_key:"sedan",category_keys:["sedan","electric"],first_registration_country_code:"PT",warranty_months:0,vat_deductible:false,published_at:"2024-01-01T00:00:00Z",badges:[],primary_photo_path:null,chat_code:null,availability:"sold",
  },
  {
    make: "Porsche",
    model: "Macan",
    id:"preview-porsche",version:null,registration_year:2021,fuel_type:"Gasolina",fuel_key:"petrol",transmission:"Automática",transmission_key:"automatic",mileage_km:49200,sale_price_cents:6990000,currency_code:"EUR",featured:false,body_type:"SUV",body_key:"suv",category_keys:["suv"],first_registration_country_code:"PT",warranty_months:12,vat_deductible:false,published_at:"2024-01-01T00:00:00Z",badges:[],primary_photo_path:null,chat_code:null,availability:"available",
  },
];

const previewCatalog:PublicVehicleCatalogData={categories:[{key:"suv",label:"SUV",count:2},{key:"city",label:"Citadino",count:1},{key:"sedan",label:"Berlina",count:1},{key:"electric",label:"Elétrico",count:1},{key:"hybrid",label:"Híbrido",count:1}],filters:{makes:["BMW","Mercedes-Benz","Porsche","Volvo"],models:samples.map(item=>item.model),fuels:[{key:"hybrid",label:"Híbrido"},{key:"diesel",label:"Diesel"},{key:"electric",label:"Elétrico"},{key:"petrol",label:"Gasolina"}],transmissions:[{key:"automatic",label:"Automática"}]},vehicles:samples,card_contract:{version:2,sizes:["compact","large","mosaic"],fields:["identity","photo","price","year","fuel","transmission","mileage","badges","availability"]}};
const emptyPreviewCatalog:PublicVehicleCatalogData={...previewCatalog,categories:[],filters:{makes:[],models:[],fuels:[],transmissions:[]},vehicles:[]};
function sampleBadges(vehicle:PublicCatalogVehicle,settings:PublicVehicleSettings){const candidates=[vehicle.featured&&"featured",new Date(vehicle.published_at??0)>=new Date(Date.now()-settings.novelty_days*86400000)&&"new",(vehicle.mileage_km??Infinity)<=settings.low_mileage_max_km&&"low_mileage",vehicle.first_registration_country_code==="PT"&&"national",vehicle.fuel_key==="electric"&&"electric",vehicle.fuel_key==="hybrid"&&"hybrid",(vehicle.warranty_months??0)>0&&"warranty",vehicle.vat_deductible&&"vat_deductible"].filter(Boolean) as string[];return settings.badge_priority.filter(key=>candidates.includes(key)).slice(0,settings.max_badges)}

export function StandSitePreviewModal({
  content,
  template,
  standName,
  profile,
  services,
  metrics,
  credit,
  financing,
  media,
  reputation,
  newsletter,
  vehicleSettings = defaultPublicVehicleSettings,
  onClose,
  onPublish,
  publishing = false,
  snapshot,
  demo = false,
}: {
  content: Content;
  template: { name: string; config: Record<string, unknown> };
  standName: string;
  profile?: TemplateStand["profile"];
  services?: TemplateStand["services"];
  metrics?: TemplateStand["metrics"];
  credit?: TemplateStand["credit"];
  financing?: TemplateStand["financing"];
  media?: TemplateStand["media"];
  reputation?: TemplateStand["reputation"];
  newsletter?: TemplateStand["newsletter"];
  vehicleSettings?: PublicVehicleSettings;
  onClose: () => void;
  onPublish?: () => void;
  publishing?: boolean;
  snapshot?: UnifiedPublicSiteSnapshot<Record<string, unknown>>;
  demo?: boolean;
}) {
  const [filters,setFilters]=useState(emptyPublicVehicleFilters);
  const activeCatalog=demo?previewCatalog:(snapshot?.catalog??emptyPreviewCatalog);
  const previewVehicles=demo?samples:(snapshot?.vehicles??[]);
  const visibleVehicles=filterPublicVehicles(previewVehicles,filters);
  const renderedVehicles=demo?visibleVehicles.map(vehicle=>({...vehicle,badges:sampleBadges(vehicle,vehicleSettings).map((key,priority)=>({key,label:publicVehicleBadgeLabels[key],reason:"Etiqueta demonstrativa",priority}))})):visibleVehicles;
  const config = template.config as {
    layout?: string;
    colors?: {
      background?: string;
      surface?: string;
      text?: string;
      accent?: string;
    };
  };
  const layout = config.layout || "avenida";
  const snapshotStand=(snapshot?.stand??{}) as Partial<TemplateStand>&{visual?:{logo?:string|null;hero?:{kind?:"image"|"video";desktop?:string|null;mobile?:string|null;fallback?:string|null}}};
  const heroVisual=snapshotStand.visual?.hero;
  const style = {
    "--stand-accent":
      content.colors.accent || config.colors?.accent || "#49f5c1",
    "--stand-background": config.colors?.background || "#061712",
    "--stand-surface": config.colors?.surface || "#0d241d",
    "--stand-text": config.colors?.text || "#f5fff9",
  } as CSSProperties;
  return (
    <div
      className="site-preview-modal"
      role="dialog"
      aria-modal="true"
      aria-label={`Pré-visualização ${template.name}`}
    >
      <header>
        <div>
          <small>PRÉ-VISUALIZAÇÃO — NÃO PUBLICADA</small>
          <strong>{template.name}</strong>
        </div>
        <div>
          <button type="button" onClick={onClose}>
            Voltar sem publicar
          </button>
          {onPublish && (
            <button
              type="button"
              className="stock-primary"
              disabled={publishing}
              onClick={onPublish}
            >
              Publicar este site
            </button>
          )}
        </div>
      </header>
      <div className="site-preview-browser">
        <div className="site-preview-browser-bar">
          <i />
          <i />
          <i />
          <span>www.seustand.pt</span>
        </div>
        <div
          className={`stand-public-site layout-${layout} site-preview-document`}
          style={style}
        >
          <StandHomeTemplate
            layout={layout}
            preview={demo}
            stand={{
              slug: snapshotStand.slug||"preview",
              name: snapshotStand.name||standName,
              legalName: snapshotStand.legalName,
              taxId: snapshotStand.taxId,
              website: snapshotStand.website,
              identityName: content.identity.name || snapshotStand.identityName || standName,
              city: snapshotStand.city||"Portugal",
              email: snapshotStand.email||undefined,
              phone: snapshotStand.phone||undefined,
              logo: snapshotStand.visual?.logo??snapshotStand.logo,
              branches: snapshotStand.branches,
              tagline: content.identity.tagline,
              headline: content.hero.headline,
              intro: content.hero.intro,
              button: content.hero.button,
              pages: content.pages,
              profile: (snapshot?.stand.profile as TemplateStand["profile"] | undefined) ?? profile,
              services: (snapshot?.stand.services as TemplateStand["services"] | undefined) ?? services,
              metrics: (snapshot?.stand.metrics as TemplateStand["metrics"] | undefined) ?? metrics,
              credit: (snapshot?.stand.credit as TemplateStand["credit"] | undefined) ?? credit,
              financing: (snapshot?.stand.financing as TemplateStand["financing"] | undefined) ?? financing,
              media: (snapshot?.stand.media as TemplateStand["media"] | undefined) ?? media,
              reputation: (snapshot?.stand.reputation as TemplateStand["reputation"] | undefined) ?? reputation,
              newsletter: (snapshot?.stand.newsletter as TemplateStand["newsletter"] | undefined) ?? newsletter,
            }}
            vehicleCount={visibleVehicles.length}
            heroMedia={heroVisual?.desktop?<ResponsiveHeroMedia kind={heroVisual.kind} desktop={heroVisual.desktop} mobile={heroVisual.mobile} fallback={heroVisual.fallback}/>:undefined}
            catalogControls={<PublicVehicleCatalogControls catalog={activeCatalog} value={filters} onChange={setFilters} resultCount={visibleVehicles.length}/>}
            catalogCategories={<PublicVehicleCategoryRail catalog={activeCatalog} value={filters} onChange={setFilters}/>}
            visitorTools={<PublicCustomerToolbar slug={snapshotStand.slug||"preview"} vehicles={previewVehicles}/>}
            vehicleCards={renderedVehicles.length?renderedVehicles.map(vehicle=><VehicleCard key={vehicle.id} slug={snapshotStand.slug||"preview"} vehicle={vehicle} imageOverride={demo?"/demo-volvo-xc40.png":undefined}/>):<div className="stand-public-empty"><h3>{previewVehicles.length?"Nenhuma viatura corresponde à pesquisa":"Novas viaturas a caminho"}</h3><p>{previewVehicles.length?"Altere ou limpe os filtros para ver outras viaturas.":"O stand ainda não publicou viaturas."}</p></div>}
          />
        </div>
      </div>
    </div>
  );
}
